import React from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from 'redux-form';
import { Dropdown } from 'semantic-ui-react';
import * as _ from 'es-toolkit';

import './MultiSelectField.css';

function selectableDropdownOptions(opts) {
    if (!opts || !opts.length)
        return [];
    return opts.filter((o) => o.value !== undefined && o.value !== null && o.value !== '');
}

function optionListIncludesValue(opts, val) {
    if (val === '' || val == null)
        return true;
    const selectable = selectableDropdownOptions(opts);
    if (!selectable.length)
        return false;
    return selectable.some(o => {
        if (Object.is(o.value, val))
            return true;
        if (String(o.value) === String(val))
            return true;
        const n = Number(val);
        return Number.isFinite(n) && Number(o.value) === n;
    });
}

/**
 * Redux-form multi-select; options from Field `props` (stable when options change).
 * Controlled value + selectOnBlur={false} to avoid stray commits from search dropdowns.
 */
function MultiSelectFieldDropdown(props) {
    const {
        input,
        options,
        placeholder,
        fixedWidth,
        selectOnBlur = false,
        className,
        hasToolTip: _hasToolTip,
        onChange: _fieldOnChange,
        ...dropdownRest
    } = props;
    const { t } = useTranslation();

    // Prevent password managers (Bitwarden/1Password/LastPass) from suggesting
    // credentials on searchable dropdowns.
    const pmIgnoreInputProps = {
        autoComplete: 'off',
        'data-bwignore': 'true',
        'data-1p-ignore': 'true',
        'data-lpignore': 'true',
    };

    const onChange = (e, data) => {
        input.onChange(data.value);
    };

    const opts = options || [];
    let dropdownOptions = opts;
    const vals = Array.isArray(input.value) ? input.value : (input.value ? [input.value] : []);
    const missing = vals.filter(v => v !== '' && v != null && !optionListIncludesValue(opts, v));
    if (missing.length) {
        const selectable_values = selectableDropdownOptions(opts).map((o) => o.value);
        dropdownOptions = opts.concat(
            _.difference(missing, selectable_values).map(id => ({
                key: id,
                text: `[${t('common.unknown')} - ${id}]`,
                className: 'line-through',
                value: id,
            }))
        );
    }

    // Semantic UI Dropdown with `multiple` requires an array, not undefined.
    const valueProp = vals.length ? vals : [];

    return (
        <Dropdown
            name={input.name}
            autoComplete="off"
            options={dropdownOptions}
            value={valueProp}
            onChange={onChange}
            placeholder={placeholder}
            fluid={!fixedWidth || fixedWidth === 'undefined'}
            multiple
            selection
            search
            scrolling
            searchInput={pmIgnoreInputProps}
            selectOnBlur={selectOnBlur}
            className={className}
            {...dropdownRest}
        />
    );
}

const MultiSelectField = ({ name, options, placeholder, fixedWidth, onSelectChange, format, parse, ...rest }) => (
    <div className="MultiSelectField">
        <Field
            multiple
            name={name}
            component={MultiSelectFieldDropdown}
            format={format}
            parse={parse}
            props={{ options, placeholder, fixedWidth, ...rest }}
            onChange={onSelectChange}
            className="form-control"
        />
    </div>
);

export default MultiSelectField;
